import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { agentsAPI, applicationsAPI, statsAPI, payoutAPI, agentStudentsAPI, agentRequestsAPI, coursesAPI, universitiesAPI, insightsAPI, userAPI, agentAgreementAPI } from '@/lib/api';
import JourneyTracker from '@/components/JourneyTracker';
import JourneyDocumentManager from '@/components/JourneyDocumentManager';
import OfferResponsePanel from '@/components/OfferResponsePanel';
import { AgentKPIPanel } from '@/components/KPIDashboard';
import Navbar from '@/components/Navbar';
import StudentDocuments from '@/components/StudentDocuments';
import PreApplyRequirementsDialog from '@/components/PreApplyRequirementsDialog';
import api from '@/lib/api';
import ForcePasswordChange from '@/components/ForcePasswordChange';
import { CommissionCalculator } from '@/components/agent/CommissionCalculator';
import { CourseBrowser } from '@/components/agent/CourseBrowser';
import { AgentBankAndDocuments } from '@/components/agent/BankAndDocuments';
import { BulkStudentUpload } from '@/components/BulkStudentUpload';
import { AddStudentDialog, PayoutRequestDialog, CourseRequestDialog } from '@/components/agent/AgentDialogs';
import AgentAgreement from '@/components/agent/AgentAgreement';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Users,
  DollarSign,
  TrendingUp,
  FileText,
  CheckCircle,
  Clock,
  Building,
  Building2,
  MapPin,
  Loader2,
  Copy,
  ExternalLink,
  Link2,
  Send,
  Wallet,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  Plus,
  GraduationCap,
  Search,
  Eye,
  BookOpen,
  MessageSquare,
  HelpCircle,
  Phone,
  Mail,
  Megaphone,
  Upload,
  Lock,
  Play,
  Calculator
} from 'lucide-react';

const AgentDashboard = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [agentProfile, setAgentProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState({});
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  // payoutForm state moved to PayoutRequestDialog for performance
  const [submittingPayout, setSubmittingPayout] = useState(false);
  
  // Force password change state
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);

  // Check if user needs to change password on mount
  useEffect(() => {
    if (user?.must_change_password) {
      setShowForcePasswordChange(true);
    }
  }, [user]);

  const handlePasswordChangeSuccess = () => {
    setShowForcePasswordChange(false);
    if (refreshUser) refreshUser();
    toast.success('Welcome! You can now access the dashboard.');
  };
  
  // New state for students and requests
  const [students, setStudents] = useState([]);
  const [agentRequests, setAgentRequests] = useState([]);
  const [showAddStudentDialog, setShowAddStudentDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  // newStudent and newRequest form state moved to extracted dialog components for performance
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [documentCheckResult, setDocumentCheckResult] = useState(null);
  const [checkingDocuments, setCheckingDocuments] = useState(false);
  const [courses, setCourses] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [courseSearchResults, setCourseSearchResults] = useState([]);
  const [searchingCourses, setSearchingCourses] = useState(false);
  const courseSearchTimer = useRef(null);
  // newStudent and newRequest state moved to extracted dialog components
  const [submittingStudent, setSubmittingStudent] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [applyingForCourse, setApplyingForCourse] = useState(null);
  const [agentPreApplyCourse, setAgentPreApplyCourse] = useState(null);
  const [agentPreApplyUniversity, setAgentPreApplyUniversity] = useState(null);
  const [agentPreApplyOpen, setAgentPreApplyOpen] = useState(false);
  const [insights, setInsights] = useState([]);
  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);
  const [selectedStudentForDocs, setSelectedStudentForDocs] = useState(null);
  
  // Agreement state
  const [agreementData, setAgreementData] = useState(null);
  
  // Change Password State
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchData();
    fetchAgreement();
  }, []);

  const fetchAgreement = async () => {
    try {
      const res = await agentAgreementAPI.get();
      setAgreementData(res.data);
    } catch (error) {
      console.error('Error fetching agreement:', error);
    }
  };

  const handleSaveAgreement = async (data) => {
    await agentAgreementAPI.save(data);
    fetchAgreement();
  };

  const handleUploadAgreement = async (file) => {
    await agentAgreementAPI.upload(file);
    fetchAgreement();
  };

  const fetchData = async () => {
    try {
      // Don't load courses/universities on mount — they're loaded on-demand via search
      const [agentRes, appsRes, statsRes, payoutsRes, studentsRes, requestsRes, insightsRes] = await Promise.all([
        agentsAPI.getMe(),
        applicationsAPI.getAll(),
        statsAPI.getDashboard(),
        payoutAPI.getMyRequests().catch(() => ({ data: [] })),
        agentStudentsAPI.getAll().catch(() => ({ data: [] })),
        agentRequestsAPI.getAll().catch(() => ({ data: [] })),
        insightsAPI.getActive().catch(() => ({ data: [] }))
      ]);
      
      setAgentProfile(agentRes.data);
      setApplications(appsRes.data || []);
      setStats(statsRes.data || {});
      setPayoutRequests(payoutsRes.data || []);
      setStudents(studentsRes.data || []);
      setAgentRequests(requestsRes.data || []);
      setInsights(insightsRes.data || []);

      // Load initial course results (just 20, not all 13k+)
      try {
        const coursesRes = await coursesAPI.search('');
        setCourseSearchResults((coursesRes.data || []).slice(0, 20));
      } catch {}
      
      // Load universities only when needed
      try {
        const uniRes = await universitiesAPI.getAll();
        setUniversities(uniRes.data || []);
      } catch {}
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const referralCode = agentProfile?.referral_code || `HED${agentProfile?.id?.slice(0, 8).toUpperCase()}`;
    const link = `${window.location.origin}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied to clipboard!');
  };

  const copyReferralCode = () => {
    const referralCode = agentProfile?.referral_code || `HED${agentProfile?.id?.slice(0, 8).toUpperCase()}`;
    navigator.clipboard.writeText(referralCode);
    toast.success('Referral code copied!');
  };

  const getPayoutStatusColor = (status) => {
    const colors = {
      pending: 'bg-teal-100 text-teal-600',
      approved: 'bg-blue-100 text-blue-600',
      paid: 'bg-green-100 text-green-600',
      rejected: 'bg-red-100 text-red-600'
    };
    return colors[status] || colors.pending;
  };

  // Check student documents before applying
  const handleOpenApplyDialog = async (student) => {
    setSelectedStudent(student);
    setCheckingDocuments(true);
    setDocumentCheckResult(null);
    setShowApplyDialog(true);
    
    try {
      const response = await agentStudentsAPI.checkDocuments(student.id);
      setDocumentCheckResult(response.data);
    } catch (error) {
      console.error('Error checking documents:', error);
      // Still allow dialog to open, error will be shown on apply attempt
    } finally {
      setCheckingDocuments(false);
    }
  };

  // Handle apply for student — opens the Pakistan-specific requirements
  // dialog first; submission happens after the agent confirms criteria match.
  const handleApplyForStudent = async (courseOrId) => {
    if (!selectedStudent) return;
    const course = typeof courseOrId === 'string'
      ? (courseSearchResults || []).find((c) => c.id === courseOrId)
      : courseOrId;
    if (!course) {
      toast.error('Course not found');
      return;
    }
    // Resolve the university for full pakistan_requirements
    let uni = null;
    try {
      if (course.university_id) {
        const res = await api.get(`/universities/${course.university_id}`);
        uni = res.data || null;
      }
    } catch (e) {
      uni = { name: course.university_name, country: course.country };
    }
    setAgentPreApplyCourse(course);
    setAgentPreApplyUniversity(uni || { name: course.university_name, country: course.country });
    setAgentPreApplyOpen(true);
  };

  const submitAgentApplication = async () => {
    if (!agentPreApplyCourse || !selectedStudent) return;
    setApplyingForCourse(agentPreApplyCourse.id);
    try {
      await agentStudentsAPI.applyForStudent(selectedStudent.id, agentPreApplyCourse.id);
      toast.success('Application submitted successfully!');
      setAgentPreApplyOpen(false);
      setAgentPreApplyCourse(null);
      setAgentPreApplyUniversity(null);
      setShowApplyDialog(false);
      setSelectedStudent(null);
      setDocumentCheckResult(null);
      setCourseSearchTerm('');
      setCourseSearchResults([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setApplyingForCourse(null);
    }
  };

  // Handle change password
  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await userAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed successfully!');
      setShowChangePassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Debounced server-side course search
  const searchCoursesFromServer = useCallback(async (term) => {
    setSearchingCourses(true);
    try {
      const res = await coursesAPI.search(term || '');
      const results = Array.isArray(res.data) ? res.data : [];
      setCourseSearchResults(term ? results : results.slice(0, 20));
    } catch (error) {
      console.error('Error searching courses:', error);
    } finally {
      setSearchingCourses(false);
    }
  }, []);

  useEffect(() => {
    if (courseSearchTimer.current) clearTimeout(courseSearchTimer.current);
    if (!courseSearchTerm.trim()) {
      // Show initial results without loading all courses
      searchCoursesFromServer('');
      return;
    }
    setSearchingCourses(true);
    courseSearchTimer.current = setTimeout(() => {
      searchCoursesFromServer(courseSearchTerm);
    }, 350);
    return () => { if (courseSearchTimer.current) clearTimeout(courseSearchTimer.current); };
  }, [courseSearchTerm, searchCoursesFromServer]);

  // Filter courses for applying
  const filteredCourses = courseSearchResults;

  // Handle submit application to H E Direct
  const handleSubmitToHEDirect = async (appId) => {
    try {
      await applicationsAPI.submitToHEDirect(appId);
      toast.success('Application submitted to H E Direct!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-600',
      submitted: 'bg-blue-100 text-blue-700',
      under_review: 'bg-amber-100 text-amber-700',
      accepted: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      enrolled: 'bg-purple-100 text-purple-700'
    };
    return colors[status] || colors.draft;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      {/* Force Password Change Modal */}
      <ForcePasswordChange 
        isOpen={showForcePasswordChange}
        onSuccess={handlePasswordChangeSuccess}
        userEmail={user?.email}
      />
      
      <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-full mx-auto">
          {/* Welcome Section - Bordered */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-6 transition-colors">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                  <Users className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-white">
                    Agent Dashboard
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400">Welcome back, {user?.full_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => window.location.href = '/training'}
                  variant="outline"
                  className="border-green-500/50 text-green-600 dark:text-green-400 hover:bg-green-500/10"
                  data-testid="training-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Training
                </Button>
                <Button
                  onClick={() => setShowChangePassword(true)}
                  variant="outline"
                  className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                  data-testid="change-password-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Change Password
                </Button>
                <Badge className="bg-teal-100 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400 text-sm px-3 py-1 border border-teal-200 dark:border-teal-800">
                  Competitive Commission
                </Badge>
              </div>
            </div>
          </div>

          {/* Commission Banner - Bordered */}
          <div className="bg-white dark:bg-slate-800 border-2 border-teal-200 dark:border-teal-800 rounded-2xl p-6 mb-6 transition-colors">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-teal-200 dark:border-teal-700">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Commission Overview</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Track your earnings and payouts</p>
              </div>
            </div>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 border border-gray-200 dark:border-slate-600">
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">Total Commission</p>
                <p className="text-3xl font-semibold text-teal-600 dark:text-teal-400">{formatCurrency(agentProfile?.total_commission || 0)}</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 border border-gray-200 dark:border-slate-600">
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">Pending Payout</p>
                <p className="text-3xl font-semibold text-gray-800 dark:text-white">{formatCurrency(agentProfile?.commission_pending || 0)}</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 border border-gray-200 dark:border-slate-600">
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">Paid Out</p>
                <p className="text-3xl font-semibold text-green-600 dark:text-green-400">{formatCurrency(agentProfile?.commission_paid || 0)}</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 border border-gray-200 dark:border-slate-600">
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">Total Referrals</p>
                <p className="text-3xl font-semibold text-gray-800 dark:text-white">{agentProfile?.total_referrals || 0}</p>
              </div>
            </div>
            
            {/* Request Payout Button */}
            {(agentProfile?.commission_pending || 0) >= 50 && agentProfile?.approval_status === 'approved' && (
              <div className="mt-4 pt-4 border-t border-teal-200 dark:border-teal-700">
                <Button
                  onClick={() => setShowPayoutDialog(true)}
                  className="bg-green-600 hover:bg-green-500 text-white font-semibold"
                  data-testid="request-payout-btn"
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  Request Payout
                </Button>
              </div>
            )}
          </div>

          {/* Referral Link Card - Bordered */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-6 transition-colors">
            {agentProfile?.approval_status !== 'approved' && (
              <div className="bg-orange-100 dark:bg-orange-900/30 border-2 border-orange-300 dark:border-orange-800 rounded-lg p-4 mb-4">
                <p className="text-orange-600 dark:text-orange-400 font-medium flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Your account is pending approval
                </p>
                <p className="text-orange-700/80 text-sm mt-1">
                  Your referral link will become active once your account is approved by the admin team.
                  Current status: <span className="font-semibold capitalize">{agentProfile?.approval_status?.replace('_', ' ') || 'pending'}</span>
                </p>
              </div>
            )}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-teal-600" />
                  Your Referral Link
                </h3>
                <p className="text-gray-500 text-sm mb-3">Share this link with students to earn commission on their applications</p>
                <div className="flex items-center gap-2 bg-[#e9ecef]/50 rounded-lg p-3">
                  <code className={`text-sm flex-1 truncate ${agentProfile?.approval_status === 'approved' ? 'text-teal-600' : 'text-gray-500'}`}>
                    {window.location.origin}/register?ref={agentProfile?.referral_code || '...'}
                  </code>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className="bg-gray-200 text-gray-800 dark:text-white">
                    Your Code: <span className={`ml-1 font-mono ${agentProfile?.approval_status === 'approved' ? 'text-teal-600' : 'text-gray-500'}`}>{agentProfile?.referral_code || '...'}</span>
                  </Badge>
                  {agentProfile?.approval_status === 'approved' && (
                    <Badge className="bg-green-100 text-green-600">Active</Badge>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  onClick={copyReferralLink}
                  className={agentProfile?.approval_status === 'approved' 
                    ? "bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed font-semibold"}
                  disabled={agentProfile?.approval_status !== 'approved'}
                  data-testid="copy-referral-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Full Link
                </Button>
                <Button
                  onClick={copyReferralCode}
                  variant="outline"
                  className={agentProfile?.approval_status === 'approved'
                    ? "border-gray-300 text-gray-800 hover:bg-gray-100"
                    : "border-gray-300 text-gray-500 cursor-not-allowed"}
                  disabled={agentProfile?.approval_status !== 'approved'}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Code Only
                </Button>
              </div>
            </div>
          </div>

          {/* Payout History */}
          {payoutRequests.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-teal-600" />
                Payout History
              </h3>
              <div className="space-y-3">
                {payoutRequests.slice(0, 5).map((payout) => (
                  <div key={payout.id} className="flex items-center justify-between p-3 bg-[#e9ecef]/50 rounded-lg">
                    <div>
                      <p className="text-gray-800 font-medium">{formatCurrency(payout.amount)}</p>
                      <p className="text-gray-500 text-xs">{new Date(payout.requested_at).toLocaleDateString()}</p>
                    </div>
                    <Badge className={getPayoutStatusColor(payout.status)}>
                      {payout.status === 'paid' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                      {payout.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                      {payout.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-gray-800 dark:text-white">{stats.my_applications || applications.length}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Applications</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                    {applications.filter(a => a.status === 'accepted' || a.status === 'enrolled').length}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Accepted</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                    {applications.filter(a => a.status === 'under_review' || a.status === 'submitted').length}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Pending</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-800 dark:text-white">Tiered</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Commission</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content with Tabs */}
          <Tabs defaultValue="students" className="space-y-6">
            <TabsList className="bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 p-1.5 rounded-xl flex-wrap">
              <TabsTrigger value="students" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-students">
                <Users className="w-4 h-4 mr-2" />
                My Students ({students.length})
              </TabsTrigger>
              <TabsTrigger value="applications" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-applications">
                <FileText className="w-4 h-4 mr-2" />
                Applications ({applications.length})
              </TabsTrigger>
              <TabsTrigger value="browse" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-browse">
                <BookOpen className="w-4 h-4 mr-2" />
                Browse Courses
              </TabsTrigger>
              <TabsTrigger value="calculator" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-calculator">
                <Calculator className="w-4 h-4 mr-2" />
                Commission Calculator
              </TabsTrigger>
              <TabsTrigger value="requests" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-requests">
                <MessageSquare className="w-4 h-4 mr-2" />
                My Requests
              </TabsTrigger>
              <TabsTrigger value="bank-docs" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="tab-bank-docs">
                <Building2 className="w-4 h-4 mr-2" />
                Bank & Documents
              </TabsTrigger>
            </TabsList>

            {/* Students Tab */}
            <TabsContent value="students">
              <div className="grid lg:grid-cols-4 gap-8">
                <div className="lg:col-span-3">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white">My Students</h2>
                    <div className="flex gap-2">
                      <BulkStudentUpload 
                        onUpload={(students) => agentStudentsAPI.bulkUpload(students)}
                        isAgent={true}
                      />
                      <Button
                        onClick={() => setShowAddStudentDialog(true)}
                        className="bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                        disabled={agentProfile?.approval_status !== 'approved'}
                        data-testid="add-student-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Student
                      </Button>
                    </div>
                  </div>
                  
                  {students.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
                      <div className="w-16 h-16 rounded-full bg-[#e9ecef] flex items-center justify-center mx-auto mb-4">
                        <Users className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">No Students Yet</h3>
                      <p className="text-gray-500 mb-6">Add students to manage their applications and earn commissions</p>
                      <Button
                        onClick={() => setShowAddStudentDialog(true)}
                        className="bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                        disabled={agentProfile?.approval_status !== 'approved'}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Your First Student
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {students.map((student) => (
                        <div key={student.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:border-teal-300 transition-colors" data-testid={`student-${student.id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4 flex-1 min-w-0">
                              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-lg font-semibold text-blue-600">{student.full_name?.charAt(0)}</span>
                              </div>
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <h4 className="text-gray-800 font-medium truncate" title={student.full_name}>{student.full_name}</h4>
                                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{student.email}</p>
                                {student.phone && <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{student.phone}</p>}
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                  <Badge className="bg-gray-200 text-gray-600 text-xs">
                                    {student.application_count || 0} Applications
                                  </Badge>
                                  {student.profile?.field_of_study && (
                                    <Badge className="bg-blue-100 text-blue-600 text-xs">
                                      {student.profile.field_of_study}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2 flex-shrink-0">
                              <Button
                                size="sm"
                                onClick={() => handleOpenApplyDialog(student)}
                                className="bg-teal-600 text-white hover:bg-teal-700"
                                data-testid={`apply-for-${student.id}`}
                              >
                                <GraduationCap className="w-4 h-4 mr-1" />
                                Apply for Course
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setSelectedStudentForDocs(student); setShowDocumentsDialog(true); }}
                                className="border-gray-300 text-gray-600 hover:bg-gray-100"
                                data-testid={`documents-${student.id}`}
                              >
                                <Upload className="w-4 h-4 mr-1" />
                                Documents
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Request University/Course Sidebar */}
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <Building className="w-5 h-5 text-teal-600" />
                      Request University/Course
                    </h3>
                    <p className="text-gray-500 text-sm mb-4">
                      Cannot find a university or course? Request H E Direct to add it to the platform.
                    </p>
                    <Button
                      onClick={() => setShowRequestDialog(true)}
                      className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-white"
                      data-testid="request-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Submit Request
                    </Button>
                  </div>

                  {/* Recent Requests */}
                  {agentRequests.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Requests</h3>
                      <div className="space-y-3">
                        {agentRequests.slice(0, 5).map((req) => (
                          <div key={req.id} className="p-3 bg-[#e9ecef]/50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <p className="text-gray-800 text-sm font-medium">{req.university_name}</p>
                              <Badge className={req.status === 'approved' ? 'bg-green-100 text-green-600' : req.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-teal-100 text-teal-600'}>
                                {req.status}
                              </Badge>
                            </div>
                            {req.course_name && <p className="text-gray-500 text-xs mt-1">{req.course_name}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* KPI Performance Panel */}
                  {agentProfile && (
                    <AgentKPIPanel agentId={agentProfile.id} />
                  )}

                  {/* Point of Contact Card - Enhanced */}
                  {agentProfile?.poc_name && (
                    <div className="bg-white border border-gray-200 rounded-xl p-6 border border-teal-300 bg-gradient-to-br from-yellow-500/5 to-transparent">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-teal-600" />
                        Your Point of Contact
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-teal-600/20 flex items-center justify-center">
                            <span className="text-xl font-semibold text-teal-600">{agentProfile.poc_name?.charAt(0)}</span>
                          </div>
                          <div>
                            <p className="text-gray-800 font-medium text-lg">{agentProfile.poc_name}</p>
                            <p className="text-gray-500 text-sm">H E Direct Consultant</p>
                          </div>
                        </div>
                        
                        {/* Quick Contact Buttons */}
                        <div className="grid grid-cols-2 gap-2 pt-2">
                          {agentProfile.poc_phone && (
                            <a 
                              href={`tel:${agentProfile.poc_phone}`} 
                              className="flex items-center justify-center gap-2 bg-green-100 hover:bg-green-500/30 text-green-600 rounded-lg py-3 px-4 transition-colors"
                              data-testid="call-poc-btn"
                            >
                              <Phone className="w-5 h-5" />
                              <span className="font-medium">Call Now</span>
                            </a>
                          )}
                          {agentProfile.poc_email && (
                            <a 
                              href={`mailto:${agentProfile.poc_email}`} 
                              className="flex items-center justify-center gap-2 bg-blue-100 hover:bg-blue-500/30 text-blue-600 rounded-lg py-3 px-4 transition-colors"
                              data-testid="email-poc-btn"
                            >
                              <Mail className="w-5 h-5" />
                              <span className="font-medium">Email</span>
                            </a>
                          )}
                        </div>

                        {/* Contact Details */}
                        <div className="space-y-2 pt-2 border-t border-gray-200">
                          {agentProfile.poc_email && (
                            <a href={`mailto:${agentProfile.poc_email}`} className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors">
                              <Mail className="w-4 h-4" />
                              <span className="text-sm">{agentProfile.poc_email}</span>
                            </a>
                          )}
                          {agentProfile.poc_phone && (
                            <a href={`tel:${agentProfile.poc_phone}`} className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors">
                              <Phone className="w-4 h-4" />
                              <span className="text-sm">{agentProfile.poc_phone}</span>
                            </a>
                          )}
                        </div>
                        
                        {/* WhatsApp Button */}
                        {agentProfile.poc_phone && (
                          <Button
                            variant="outline"
                            className="w-full border-teal-400 text-teal-600 hover:bg-teal-600/10"
                            onClick={() => window.open(`https://wa.me/${agentProfile.poc_phone?.replace(/[^0-9]/g, '')}`, '_blank')}
                            data-testid="whatsapp-poc-btn"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            WhatsApp Contact
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Insights/Announcements */}
                  {insights.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Megaphone className="w-5 h-5 text-teal-600" />
                        Latest Updates
                      </h3>
                      <div className="space-y-3">
                        {insights.slice(0, 3).map((insight) => (
                          <div key={insight.id} className={`p-3 rounded-lg ${insight.priority === 'urgent' ? 'bg-red-500/10 border border-red-500/30' : insight.priority === 'important' ? 'bg-teal-500/10 border border-teal-300' : 'bg-[#e9ecef]/50'}`}>
                            <p className="text-gray-800 text-sm font-medium">{insight.title}</p>
                            <p className="text-gray-500 text-xs mt-1 line-clamp-2">{insight.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Agent Profile */}
                  {agentProfile && (
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Agent Profile</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Company</span>
                          <span className="text-gray-800 dark:text-white">{agentProfile.company_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Location</span>
                          <span className="text-gray-800 dark:text-white">{agentProfile.city}, {agentProfile.country}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Status</span>
                          <Badge className={agentProfile.approval_status === 'approved' ? 'bg-green-100 text-green-600' : 'bg-teal-100 text-teal-600'}>
                            {agentProfile.approval_status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Resources Links */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Resources</h3>
                    <div className="space-y-2">
                      <Link to="/blog" className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#e9ecef]/50 transition-colors">
                        <BookOpen className="w-5 h-5 text-blue-600" />
                        <span className="text-gray-600 dark:text-gray-300">Blog & Articles</span>
                      </Link>
                      <Link to="/help" className="flex items-center gap-3 p-3 rounded-lg hover:bg-[#e9ecef]/50 transition-colors">
                        <HelpCircle className="w-5 h-5 text-green-600" />
                        <span className="text-gray-600 dark:text-gray-300">Help & Support</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Applications Tab */}
            <TabsContent value="applications">
              <div className="grid lg:grid-cols-4 gap-8">
                <div className="lg:col-span-3">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-6">All Applications</h2>
                  
                  {applications.length === 0 ? (
                    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-8 text-center">
                      <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
                        <FileText className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">No Applications Yet</h3>
                      <p className="text-gray-500 dark:text-gray-400">Add students and apply for courses to see applications here</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {applications.map((app) => (
                        <div 
                          key={app.id} 
                          className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-5 hover:border-teal-300 transition-colors cursor-pointer" 
                          data-testid={`agent-app-${app.id}`}
                          onClick={() => window.location.href = `/application/${app.id}`}
                        >
                          {/* Application Card - Simplified View */}
                          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-3">
                                <Badge className="bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-xs">
                                  {(app.journey_stage || 'inquiry').replace(/_/g, ' ')}
                                </Badge>
                                <Badge className={getStatusColor(app.status)}>
                                  {app.status?.replace('_', ' ')}
                                </Badge>
                                {app.submitted_to_hedirect ? (
                                  <Badge className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Submitted
                                  </Badge>
                                ) : (
                                  <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                                    Pending
                                  </Badge>
                                )}
                              </div>
                              <h4 className="text-gray-800 dark:text-white font-medium text-lg mb-1">{app.course_name}</h4>
                              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                                <Building className="w-3 h-3" />
                                {app.university_name}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                Student: {app.student_name || app.student_email}
                              </p>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex flex-col gap-2">
                              {!app.submitted_to_hedirect && (
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSubmitToHEDirect(app.id);
                                  }}
                                  className="bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                                  data-testid={`submit-app-${app.id}`}
                                >
                                  <Send className="w-4 h-4 mr-2" />
                                  Submit
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.location.href = `/application/${app.id}`;
                                }}
                                className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View Journey
                              </Button>
                              <p className="text-xs text-gray-500 dark:text-gray-400 text-right">
                                Created: {new Date(app.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Commission Info Sidebar */}
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Commission Structure</h3>
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 p-4 bg-[#e9ecef]/50 rounded-lg">
                        <div className="w-12 h-12 rounded-lg bg-teal-600/20 flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium">Competitive Commission</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">The more you recruit, the more you earn</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 p-4 bg-[#e9ecef]/50 rounded-lg">
                        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Building className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium">H E Direct Support</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Platform fee</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Support */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Need Help?</h3>
                    <p className="text-gray-500 text-sm mb-4">Contact our agent support team for assistance</p>
                    <Button
                      variant="outline"
                      className="w-full border-gray-300 text-gray-600 hover:bg-gray-100"
                      onClick={() => window.open('https://wa.me/447488420729', '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Contact Support
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Browse Courses Tab */}
            <TabsContent value="browse">
              <CourseBrowser 
                universities={universities}
                courses={courses}
                onApplyForStudent={null}
                selectedStudent={null}
              />
            </TabsContent>

            {/* Commission Calculator Tab */}
            <TabsContent value="calculator">
              <CommissionCalculator />
            </TabsContent>

            {/* Requests Tab */}
            <TabsContent value="requests">
              <div className="grid lg:grid-cols-4 gap-8">
                <div className="lg:col-span-3">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white">University/Course Requests</h2>
                    <Button
                      onClick={() => setShowRequestDialog(true)}
                      className="bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Request
                    </Button>
                  </div>
                  
                  {agentRequests.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
                      <div className="w-16 h-16 rounded-full bg-[#e9ecef] flex items-center justify-center mx-auto mb-4">
                        <Building className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">No Requests Yet</h3>
                      <p className="text-gray-500 mb-6">Request universities or courses that are not available on the platform</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {agentRequests.map((req) => (
                        <div key={req.id} className="bg-white border border-gray-200 rounded-xl p-5" data-testid={`request-${req.id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Badge className={req.request_type === 'university' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}>
                                  {req.request_type}
                                </Badge>
                                <Badge className={req.status === 'approved' ? 'bg-green-100 text-green-600' : req.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-teal-100 text-teal-600'}>
                                  {req.status}
                                </Badge>
                              </div>
                              <h4 className="text-gray-800 font-medium truncate" title={req.university_name}>{req.university_name}</h4>
                              {req.course_name && <p className="text-gray-500 text-sm truncate">Course: {req.course_name}</p>}
                              {req.country && <p className="text-gray-500 text-sm">Country: {req.country}</p>}
                              {req.admin_notes && (
                                <p className="text-gray-500 text-sm mt-2 p-2 bg-[#e9ecef]/50 rounded">
                                  Admin: {req.admin_notes}
                                </p>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                              {new Date(req.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">How It Works</h3>
                    <div className="space-y-4 text-sm">
                      <div className="flex gap-3">
                        <div className="w-6 h-6 rounded-full bg-teal-600/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-teal-600 font-semibold text-xs">1</span>
                        </div>
                        <p className="text-gray-500 dark:text-gray-400">Submit a request for a university or specific course</p>
                      </div>
                      <div className="flex gap-3">
                        <div className="w-6 h-6 rounded-full bg-teal-600/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-teal-600 font-semibold text-xs">2</span>
                        </div>
                        <p className="text-gray-500 dark:text-gray-400">H E Direct team reviews and verifies the institution</p>
                      </div>
                      <div className="flex gap-3">
                        <div className="w-6 h-6 rounded-full bg-teal-600/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-teal-600 font-semibold text-xs">3</span>
                        </div>
                        <p className="text-gray-500 dark:text-gray-400">Once approved, the university/course becomes available for applications</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Bank & Documents Tab */}
            <TabsContent value="bank-docs">
              <div className="space-y-6">
                {/* Partner Agreement Section */}
                <AgentAgreement
                  agentId={agentProfile?.id}
                  agentName={agentProfile?.full_name || user?.full_name}
                  agentEmail={agentProfile?.email || user?.email}
                  agentPhone={agentProfile?.phone}
                  agentAddress={agentProfile?.address}
                  agreementData={agreementData}
                  onSave={handleSaveAgreement}
                  onUpload={handleUploadAgreement}
                  isAdmin={false}
                  readOnly={false}
                />
                
                {/* Bank Details & Documents */}
                <AgentBankAndDocuments />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

            {/* Payout Request Dialog — extracted for performance */}
      <PayoutRequestDialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog} availableBalance={agentProfile?.commission_pending || 0} onSuccess={fetchData} />

      {/* Add Student Dialog — extracted for performance */}
      <AddStudentDialog open={showAddStudentDialog} onOpenChange={setShowAddStudentDialog} onSuccess={fetchData} />

      {/* Request University/Course Dialog — extracted for performance */}
      <CourseRequestDialog open={showRequestDialog} onOpenChange={setShowRequestDialog} onSuccess={fetchData} />

      {/* Apply for Course Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={(open) => {
        setShowApplyDialog(open);
        if (!open) {
          setDocumentCheckResult(null);
          setSelectedStudent(null);
        }
      }}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-teal-600" />
              Apply for Course - {selectedStudent?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {/* Document Status Banner */}
            {checkingDocuments ? (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span className="text-blue-700 text-sm">Checking required documents...</span>
              </div>
            ) : documentCheckResult && !documentCheckResult.can_apply ? (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-red-700 font-medium text-sm">Missing Required Documents</p>
                    <p className="text-red-600 text-sm mt-1">
                      Please upload the following documents before applying: {documentCheckResult.missing_documents?.map(d => d === 'cv' ? 'CV/Resume' : d === 'passport' ? 'Passport' : 'Academic Qualification').join(', ')}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-2 border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => {
                        setShowApplyDialog(false);
                        setSelectedStudentForDocs(selectedStudent);
                        setShowDocumentsDialog(true);
                      }}
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      Upload Documents
                    </Button>
                  </div>
                </div>
              </div>
            ) : documentCheckResult?.can_apply ? (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span className="text-green-700 text-sm">All required documents uploaded ({documentCheckResult.total_documents} documents)</span>
              </div>
            ) : null}
            
            {/* Search */}
            <div className="mb-4">
              <Label className="text-gray-600 dark:text-gray-300">Search Courses</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
                <Input
                  value={courseSearchTerm}
                  onChange={(e) => setCourseSearchTerm(e.target.value)}
                  placeholder="Search by course name, university, or field..."
                  className="bg-white border-gray-200 text-gray-800 pl-10"
                />
              </div>
            </div>

            {/* Course List */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {searchingCourses ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">Searching courses...</p>
                </div>
              ) : filteredCourses.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">
                    {courseSearchTerm ? 'No courses found matching your search' : 'Type to search from 13,000+ courses'}
                  </p>
                </div>
              ) : (
                filteredCourses.map((course) => (
                  <div key={course.id} className="bg-white border border-gray-200 rounded-xl rounded-lg p-4 hover:border-teal-300 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-gray-800 font-medium mb-1">{course.name}</h4>
                        <p className="text-gray-500 text-sm mb-2 flex items-center gap-1">
                          <Building className="w-3 h-3" />
                          {course.university_name}
                        </p>
                        <div className="flex items-center gap-2 mb-2">
                          {course.field_of_study && (
                            <Badge className="bg-blue-100 text-blue-600 text-xs">
                              {course.field_of_study}
                            </Badge>
                          )}
                          {course.level && (
                            <Badge className="bg-purple-100 text-purple-600 text-xs">
                              {course.level}
                            </Badge>
                          )}
                          {course.country && (
                            <Badge className="bg-gray-300 text-gray-600 text-xs">
                              {course.country}
                            </Badge>
                          )}
                        </div>
                        {course.tuition_fee && (
                          <p className="text-green-600 text-sm font-medium">
                            {course.tuition_fee}
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleApplyForStudent(course.id)}
                        disabled={applyingForCourse === course.id}
                        className="bg-teal-600 text-white hover:bg-teal-700"
                      >
                        {applyingForCourse === course.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Apply'
                        )}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Student Documents Dialog */}
      <StudentDocuments
        student={selectedStudentForDocs}
        open={showDocumentsDialog}
        onClose={() => { setShowDocumentsDialog(false); setSelectedStudentForDocs(null); }}
      />

      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <Lock className="w-5 h-5 text-teal-600" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">Current Password</Label>
              <Input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                className="bg-[#e9ecef] border-gray-200"
                placeholder="Enter current password"
                data-testid="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">New Password</Label>
              <Input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                className="bg-[#e9ecef] border-gray-200"
                placeholder="Enter new password (min 6 characters)"
                data-testid="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-600 dark:text-gray-300">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                className="bg-[#e9ecef] border-gray-200"
                placeholder="Confirm new password"
                data-testid="confirm-password"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                className="flex-1 border-gray-300 text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={passwordLoading}
                className="flex-1 bg-teal-600 text-white hover:bg-teal-700 font-semibold"
                data-testid="save-password-btn"
              >
                {passwordLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Update Password
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PreApplyRequirementsDialog
        open={agentPreApplyOpen}
        onOpenChange={setAgentPreApplyOpen}
        university={agentPreApplyUniversity}
        course={agentPreApplyCourse}
        submitting={applyingForCourse === agentPreApplyCourse?.id}
        onConfirm={submitAgentApplication}
      />
    </div>
  );
};

export default AgentDashboard;
